import axios from 'axios';
import qs from 'qs';
import {
    createInfiniteQuery,
    createMutation,
    createQuery,
} from 'react-query-kit';

import { HTTP_HEADERS } from '@/constants';
import { restUrl } from '@/services/utils';
import { ServerErrorResponse } from '@/types/ServerErrorResponse';

import {
    EnhancedContentDataType,
    EnhancedContentRetailer,
    LayoutBlockDefinitionType,
} from './enhanced-content.types';

interface EnhancedContentQueryVariables {
    limit: number;
    keywords?: string;
}

interface EnhancedContentQueryResponse {
    data: EnhancedContentDataType[];
    cursor?: string;
}

interface EnhancedContentStatistics {
    countAll: number;
    countPublished: number;
}
const ASSETS_END_POINT = 'assets';
const END_POINT = 'enhancedContent';

const useEnhancedContentData =
    createInfiniteQuery<EnhancedContentQueryResponse, EnhancedContentQueryVariables, Error, string | undefined>({
        queryKey: [END_POINT],
        fetcher: async (variables, { pageParam }) => {

            const response = await axios.get(restUrl(END_POINT), {
                params: variables,
                headers: {
                    [HTTP_HEADERS.X_CURSOR]: pageParam
                },
                paramsSerializer: (params) => {
                    // We need to remove empty params i.e. '', as it breaks the endpoint.
                    const filteredParams = Object.keys(params)
                        .reduce((allParams: Partial<Record<string, any>>, currentParam: string) => {
                            const paramValue = params[currentParam];
                            if (paramValue && paramValue != '') {
                                allParams[currentParam] = paramValue;
                            }

                            return allParams;
                        }, []);

                    return qs.stringify(filteredParams);
                }
            });

            return {
                data: response.data?? [],
                cursor: response.headers[HTTP_HEADERS.X_CURSOR]?? undefined
            };
        },
        getNextPageParam: (lastPageRequestResponse) => lastPageRequestResponse.cursor,
        initialPageParam: undefined
    });

const useCreateEnhancedContentEntry =
    createMutation<EnhancedContentDataType, { entry : EnhancedContentDataType}, ServerErrorResponse>({
        mutationFn: async ({entry}) => {
            const response = await axios
                .post(restUrl(END_POINT), {enhancedContent: entry, resetApproved: true });
            return response.data;
        }
    });

const useUpdateEnhancedContentEntry =
    createMutation<EnhancedContentDataType, { entry : EnhancedContentDataType }, ServerErrorResponse>({
        mutationFn: async ({entry}) => {
            const response = await axios.
                put(restUrl(`${END_POINT}/${entry.id}`), {enhancedContent: entry, resetApproved: true });
            return response.data;
        }
    });

const usePublishEnhancedContentEntry =
    createMutation<EnhancedContentDataType, { id: number }, ServerErrorResponse>({
        mutationFn: async (entry : { id: number }) => {
            const response = await axios.post(`${restUrl(END_POINT)}/${entry.id}/publish`);

            return response.data;
        }
    });

const useApprovalEnhancedContentEntry =
    createMutation<EnhancedContentDataType, { id: number, approve: boolean }, ServerErrorResponse>({
        mutationFn: async (entry : { id: number, approve: boolean }) => {
            const url = `${restUrl(END_POINT)}/${entry.id}/approval`;
            const response = await axios.put(url, {approve: entry.approve});

            return response.data;
        }
    });

const useDeleteEnhancedContentEntry =
    createMutation<unknown, { id: number }>({
        mutationFn: async (entry: { id: number }) => {
            const response = await axios.delete(`${restUrl(END_POINT)}/${entry.id}`);

            return response.data;
        }
    });

const useEnhancedContentLayoutBlocks =
    createQuery<LayoutBlockDefinitionType[], void, Error>({
        queryKey: [`${END_POINT}/_layoutBlockTemplates`],
        fetcher: () => {
            return axios.get(restUrl(`${END_POINT}/_layoutBlockTemplates`)).then(response => response.data);
        }
    });

const useEnhancedContentRetailers =
    createQuery<EnhancedContentRetailer[], void, Error>({
        queryKey: [`${END_POINT}/_retailers`],
        fetcher: () => {
            return axios.get(restUrl(`${END_POINT}/_retailers`)).then(response => response.data);
        }
    });

const usePublicAssetUploader =
    createMutation({
        mutationFn: async (entry: { file: File }) => {

            const formDataToSend = new FormData();

            formDataToSend.append('publicAsset', 'true');
            formDataToSend.append('file', entry.file);

            const response = await axios.post(restUrl(ASSETS_END_POINT), formDataToSend);

            return response.data;
        }
    });

const useEnhancedContentStatistics =
    createQuery<EnhancedContentStatistics, void, Error>({
        queryKey: [`${END_POINT}/statistics`],
        fetcher: () => {
            return axios.get(restUrl(`${END_POINT}/statistics`)).then(response => response.data);
        }
    });

export {
    useApprovalEnhancedContentEntry,
    useCreateEnhancedContentEntry,
    useDeleteEnhancedContentEntry,
    useEnhancedContentData,
    useEnhancedContentLayoutBlocks,
    useEnhancedContentRetailers,
    useEnhancedContentStatistics,
    usePublicAssetUploader,
    usePublishEnhancedContentEntry,
    useUpdateEnhancedContentEntry
};
